import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { CheckIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { UserProfile, PendingFriendRequest, Friend } from '../types'; // Import shared types

// Define props for Profile component
interface ProfileProps {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

export default function Profile({ isDarkMode, toggleDarkMode }: ProfileProps) { // Accept props
  const { user: authUser } = useAuth(); // Get the authenticated user context
  const { addNotification } = useNotification();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [pendingRequests, setPendingRequests] = useState<PendingFriendRequest[]>([]); // Use imported type
  const [friends, setFriends] = useState<Friend[]>([]); // Use imported type
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [aboutMeInput, setAboutMeInput] = useState(''); // State for textarea input
  const [isEditingAboutMe, setIsEditingAboutMe] = useState(false); // State to toggle edit mode
  // State for password change form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState('');
  // Action loading states
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    if (authUser) {
      setIsLoading(true); // Set loading true when we start fetching
      fetchProfileData();
      if (!authUser.isGuest) {
        // Use Promise.all to wait for both friend fetches before setting loading false
        Promise.all([fetchPendingRequests(), fetchFriends()]).finally(() => {
          setIsLoading(false);
        });
      } else {
        setIsLoading(false); // Guests don't fetch friends, so set loading false after profile
      }
    } else {
       // If authUser is null/undefined initially, keep loading until it resolves
       setIsLoading(true);
    }
  }, [authUser]);

  const fetchProfileData = async () => {
     setIsLoading(true);
     try {
       const response = await fetch('/api/auth/me', { credentials: 'include' });
       if (!response.ok) {
          const errData = await response.json() as { error: string };
          throw new Error(errData.error || 'Failed to fetch profile');
       }
       const data = await response.json() as UserProfile;
       setProfile(data); // Fetched data now includes created_at
       setAboutMeInput(data.about_me || ''); 
     } catch (err) {
       setError(err instanceof Error ? err.message : 'Could not load profile');
       setProfile(null); 
     } finally {
        // Loading handled by useEffect
     }
  };

  const fetchPendingRequests = async () => {
    // setIsLoading(true); // Remove loading state management from individual fetches
    try {
      const response = await fetch('/api/friends/requests/pending', { credentials: 'include' });
      const data = await response.json() as PendingFriendRequest[] | { error: string };
      if (!response.ok) throw new Error((data as {error: string}).error || 'Failed to fetch pending requests');
      setPendingRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading pending requests');
    } finally {
      // setIsLoading(false); // Remove loading state management from individual fetches
    }
   };
  const fetchFriends = async () => {
    // setIsLoading(true); // Remove loading state management from individual fetches
    try {
      const response = await fetch('/api/friends', { credentials: 'include' });
      const data = await response.json() as Friend[] | { error: string };
      if (!response.ok) throw new Error((data as {error: string}).error || 'Failed to fetch friends');
      setFriends(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading friends list');
    } finally {
       // setIsLoading(false); // Remove loading state management from individual fetches
    }
  };
  const handleAcceptRequest = async (senderId: string) => {
    setError('');
    try {
      const response = await fetch(`/api/friends/requests/${senderId}`, {
        method: 'PUT',
        credentials: 'include'
      });
      const data = await response.json() as { error?: string; message?: string };
      if (!response.ok) throw new Error(data.error || 'Failed to accept request');
      addNotification('Friend request accepted!', 'success');
      fetchPendingRequests();
      fetchFriends();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to accept request';
      setError(errorMsg);
      addNotification(errorMsg, 'error');
    }
  };
  const handleDeclineRequest = async (senderId: string) => {
    setError('');
    try {
      const response = await fetch(`/api/friends/requests/${senderId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json() as { error?: string; message?: string };
      if (!response.ok) throw new Error(data.error || 'Failed to decline request');
      addNotification('Friend request declined.', 'info');
      fetchPendingRequests();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to decline request';
      setError(errorMsg);
      addNotification(errorMsg, 'error');
    }
  };
  const handleRemoveFriend = async (friendId: string) => {
    if (!window.confirm('Are you sure you want to remove this friend?')) return;
    setError('');
     try {
      const response = await fetch(`/api/friends/${friendId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json() as { error?: string; message?: string };
      if (!response.ok) throw new Error(data.error || 'Failed to remove friend');
      addNotification('Friend removed.', 'success');
      fetchFriends();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to remove friend';
      setError(errorMsg);
      addNotification(errorMsg, 'error');
    }
  };

  const handleUpdateProfile = async () => {
    setError('');
    setIsUpdatingProfile(true); // Set loading true
    try {
        const response = await fetch('/api/me/profile', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ about_me: aboutMeInput })
        });
        const data = await response.json() as { error?: string; message?: string; about_me?: string | null };
        if (!response.ok) {
            throw new Error(data.error || 'Failed to update profile');
        }
        // Update local profile state optimistically or based on response
        setProfile(prev => prev ? { ...prev, about_me: data.about_me ?? aboutMeInput } : null);
        setIsEditingAboutMe(false); // Exit edit mode
        addNotification('Profile updated!', 'success');
    } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to update profile';
        setError(errorMsg);
        addNotification(errorMsg, 'error');
    } finally {
        setIsUpdatingProfile(false); // Set loading false
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordChangeError('');
    setPasswordChangeSuccess('');
    setIsChangingPassword(true); // Set loading true

    if (newPassword !== confirmPassword) {
      setPasswordChangeError("New passwords don't match.");
      return;
    }
    if (newPassword.length < 6) {
        setPasswordChangeError("New password must be at least 6 characters long.");
        return;
    }

    try {
      const response = await fetch('/api/me/password', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currentPassword, newPassword, confirmPassword })
      });
      const data = await response.json() as { error?: string; message?: string; details?: any };

      if (!response.ok) {
        let errMsg = data.error || 'Failed to change password';
        // Handle detailed validation errors from Zod
        if (data.details) {
            const fieldErrors = Object.values(data.details).flat().join(', ');
            errMsg = `Validation failed: ${fieldErrors}`; 
        }
        throw new Error(errMsg);
      }

      setPasswordChangeSuccess(data.message || 'Password updated successfully!');
      addNotification(data.message || 'Password updated successfully!', 'success');
      // Clear fields on success
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An unknown error occurred';
      setPasswordChangeError(errorMsg);
      addNotification(errorMsg, 'error');
    } finally {
        setIsChangingPassword(false); // Set loading false
    }
  };

  if (isLoading) {
    return <div className="text-center p-10">Loading profile...</div>; 
  }

  if (!profile) {
    return <div className="text-center p-10 text-red-600">Could not load profile information.</div>;
  }

  // Basic structure - will add sections later
  return (
    <div className="space-y-8">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Your Profile</h1>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Section 1: Basic Info */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold mb-4 dark:text-gray-100">Account Information</h2>
        <div className="space-y-3">
          <p className="dark:text-gray-300"><strong>Username:</strong> {profile.username}</p>
          {!profile.isGuest && <p className="dark:text-gray-300"><strong>Email:</strong> {profile.email || 'Not set'}</p>}
          <p className="dark:text-gray-300"><strong>Account Type:</strong> {profile.isGuest ? 'Guest' : 'Registered'}</p>
          <p className="dark:text-gray-300"><strong>Joined:</strong> {profile.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}</p>
          
          {/* About Me Section */} 
          {!profile.isGuest && (
             <div className="pt-3">
                <label htmlFor="aboutMe" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  About Me
                </label>
                {isEditingAboutMe ? (
                    <textarea
                        id="aboutMe"
                        rows={4}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        value={aboutMeInput}
                        onChange={(e) => setAboutMeInput(e.target.value)}
                    />
                ) : (
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {profile.about_me || <span className="text-gray-400 italic">No information provided.</span>}
                    </p>
                )}
             </div>
          )}

          {/* Edit/Save Buttons for About Me */} 
          {!profile.isGuest && (
            <div className="flex justify-end space-x-3 mt-4">
              {isEditingAboutMe ? (
                <>
                  <button onClick={() => { setIsEditingAboutMe(false); setAboutMeInput(profile.about_me || ''); }} className="btn-secondary">
                     Cancel
                  </button>
                  <button 
                    onClick={handleUpdateProfile} 
                    className="btn-primary"
                    disabled={isUpdatingProfile} // Disable when loading
                   >
                    {isUpdatingProfile ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                    ) : (
                      'Save Changes'
                    )} 
                   </button>
                </>
              ) : (
                <button onClick={() => setIsEditingAboutMe(true)} className="btn-secondary">
                  Edit About Me
                </button>
              )}
            </div>
           )}
        </div>
      </div>

      {/* Section 2: Appearance (Dark Mode) */}
      <div className="card p-6">
         <h2 className="text-xl font-semibold mb-4 dark:text-gray-100">Appearance</h2>
         <div className="flex items-center justify-between">
           <span className="text-gray-700 dark:text-gray-200">Dark Mode</span>
           <button 
             onClick={toggleDarkMode}
             className={`${isDarkMode ? 'bg-primary' : 'bg-gray-200'} relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light`}
           >
             <span className="sr-only">Toggle Dark Mode</span>
             <span 
               className={`${isDarkMode ? 'translate-x-6' : 'translate-x-1'} inline-block w-4 h-4 transform bg-white rounded-full transition-transform`}
             />
           </button>
         </div>
      </div>

      {/* Section 3: Friends (Only for registered users) */}
      {!profile.isGuest && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Pending Requests */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold mb-4 dark:text-gray-100">Pending Friend Requests</h2>
            {isLoading ? ( // Use main isLoading here
              <div className="space-y-3 animate-pulse">
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            ) : pendingRequests.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">No pending requests.</p>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {pendingRequests.map((req) => (
                  <li key={req.sender_id} className="flex items-center justify-between py-2">
                    <span className="dark:text-gray-200">
                      <span className="font-medium dark:text-gray-100">{req.sender_username}</span>
                    </span>
                    <div className="space-x-2">
                      <button onClick={() => handleAcceptRequest(req.sender_id)} className="btn-primary p-1 text-xs" title="Accept">
                        <CheckIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDeclineRequest(req.sender_id)} className="btn-secondary p-1 text-xs" title="Decline">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Friends List */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold mb-4 dark:text-gray-100">Friends</h2>
             {isLoading ? ( // Use main isLoading here
               <div className="space-y-3 animate-pulse">
                 <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                 <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                 <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
               </div>
            ) : friends.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">No friends yet.</p>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {friends.map((friend) => (
                  <li key={friend.friend_id} className="flex items-center justify-between py-2">
                     <span className="font-medium dark:text-gray-100">{friend.friend_username}</span>
                     <button 
                        onClick={() => handleRemoveFriend(friend.friend_id)}
                        className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-100 text-xs"
                        title="Remove Friend"
                     >
                       <TrashIcon className="h-4 w-4" />
                     </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Section 4: Security */}
      {!profile.isGuest && (
        <div className="card p-6">
          <h2 className="text-xl font-semibold mb-4 dark:text-gray-100">Security</h2>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Current Password
              </label>
              <input
                type="password"
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              />
            </div>
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              />
            </div>

            {passwordChangeError && (
              <p className="text-sm text-red-600 dark:text-red-400 mt-2">{passwordChangeError}</p>
            )}
            {passwordChangeSuccess && (
              <p className="text-sm text-green-600 dark:text-green-400 mt-2">{passwordChangeSuccess}</p>
            )}

            <div className="flex justify-end">
              <button 
                type="submit" 
                className="btn-primary"
                disabled={isChangingPassword} // Disable when loading
               >
                {isChangingPassword ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Changing...
                    </span>
                  ) : (
                    'Change Password'
                  )} 
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Section 5: API Tokens (Placeholder) */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold mb-4 dark:text-gray-100">API Tokens</h2>
        <p className="text-gray-500 dark:text-gray-400 italic">
          API token management will be available here in the future for third-party integrations.
        </p>
        {/* TODO: Add API Token generation/listing/revoking UI */}
      </div>
      
      {/* Section 6: Account/Payment (Placeholder) */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold mb-4 dark:text-gray-100">Account & Billing</h2>
        <p className="text-gray-500 dark:text-gray-400 italic">
          Subscription and billing details will appear here if applicable.
        </p>
        {/* TODO: Add billing details, subscription management, etc. */}
      </div>

    </div>
  );
} 